if (!customElements.get('bundle-products')) {
  class BundleProducts extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.checkboxes = this.querySelectorAll('.bundle-checkbox');
      this.addButton = this.querySelector('.bundle-products__button');
      this.selectedCountEl = this.querySelector('.bundle-selected-count');
      this.totalPriceEl = this.querySelector('.bundle-products__total-current');
      this.errorEl = this.querySelector('.bundle-products__error');
      this.errorTextEl = this.querySelector('.bundle-products__error-text');
      this.cartDrawer = document.querySelector('cart-drawer');

      this.checkboxes.forEach((checkbox) => {
        checkbox.addEventListener('change', this.updateSelection.bind(this));
      });

      this.addButton.addEventListener('click', this.handleAddToCart.bind(this));
    }

    /**
     * Updates the selected count and total price when checkboxes change
     */
    updateSelection() {
      const checkedBoxes = Array.from(this.checkboxes).filter((cb) => cb.checked);
      const count = checkedBoxes.length;

      // Update selected count
      this.selectedCountEl.textContent = count;

      // Calculate total price
      const totalPrice = checkedBoxes.reduce((sum, cb) => {
        return sum + parseInt(cb.dataset.price, 10);
      }, 0);

      // Update total price display
      const formattedPrice = this.formatMoney(totalPrice);
      this.totalPriceEl.textContent = formattedPrice;

      // Disable button if no products selected
      if (count === 0) {
        this.addButton.setAttribute('disabled', 'disabled');
      } else {
        this.addButton.removeAttribute('disabled');
      }

      // Hide error when selection changes
      this.hideError();
    }

    /**
     * Handles adding selected products to cart
     */
    async handleAddToCart(evt) {
      evt.preventDefault();

      if (this.addButton.hasAttribute('disabled')) return;

      const checkedBoxes = Array.from(this.checkboxes).filter((cb) => cb.checked);
      
      if (checkedBoxes.length === 0) {
        this.showError('Please select at least one product');
        return;
      }

      // Disable button and show loading state
      this.addButton.setAttribute('disabled', 'disabled');
      this.addButton.classList.add('is-loading');
      this.hideError();

      try {
        // Add all selected products to cart
        const addPromises = checkedBoxes.map((checkbox) => {
          const variantId = checkbox.dataset.variantId;
          return this.addToCart(variantId, 1);
        });

        await Promise.all(addPromises);

        // Show success state
        this.addButton.classList.add('is-success');

        // Update cart
        await this.updateCart();

        // Reset button after delay
        setTimeout(() => {
          this.addButton.classList.remove('is-loading');
          this.addButton.classList.remove('is-success');
          this.addButton.removeAttribute('disabled');
        }, 1400);

      } catch (error) {
        console.error('Error adding to cart:', error);
        this.showError(error.message || 'Error adding products to cart');
        
        this.addButton.classList.remove('is-loading');
        this.addButton.removeAttribute('disabled');
      }
    }

    /**
     * Adds a single product to cart
     * @param {string} variantId - The variant ID to add
     * @param {number} quantity - The quantity to add
     * @returns {Promise}
     */
    async addToCart(variantId, quantity) {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);

      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', ')).join(', ');
        }
        throw new Error(error);
      }

      return data;
    }

    /**
     * Updates the cart drawer or page
     */
    async updateCart() {
      // Update cart icon bubble
      try {
        const response = await fetch(`${theme.routes.cart}.js`);
        if (!response.ok) throw new Error(response.status);
        
        const cartData = await response.json();

        // Update cart icon
        const cartIconBubbleResponse = await fetch('?sections=cart-icon-bubble');
        if (cartIconBubbleResponse.ok) {
          const sections = await cartIconBubbleResponse.json();
          const cartIconBubble = document.getElementById('cart-icon-bubble');
          if (cartIconBubble && sections['cart-icon-bubble']) {
            cartIconBubble.innerHTML = sections['cart-icon-bubble'];
          }
        }

        // Open cart drawer if it exists and is configured to open
        if (this.cartDrawer && theme.settings.afterAtc === 'drawer') {
          setTimeout(() => {
            this.cartDrawer.refresh(true);
          }, 700);
        } else if (theme.settings.afterAtc === 'page') {
          // Redirect to cart page
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else if (window.location.pathname === theme.routes.cart) {
          // If we're on the cart page, refresh it
          const cartItems = document.querySelector('cart-items');
          if (cartItems) {
            if (cartItems.dataset.empty === 'true') {
              window.location.reload();
            } else {
              cartItems.refresh();
            }
          }
        }

        // Dispatch custom event
        this.dispatchEvent(new CustomEvent('on:bundle:add', {
          bubbles: true,
          detail: {
            cart: cartData,
            count: this.checkboxes.length
          }
        }));

      } catch (error) {
        console.error('Error updating cart:', error);
      }
    }

    /**
     * Shows an error message
     * @param {string} message - The error message to display
     */
    showError(message) {
      if (this.errorEl && this.errorTextEl) {
        this.errorTextEl.textContent = message;
        this.errorEl.hidden = false;
      }
    }

    /**
     * Hides the error message
     */
    hideError() {
      if (this.errorEl) {
        this.errorEl.hidden = true;
      }
    }

    /**
     * Formats money value using theme settings
     * @param {number} cents - Price in cents
     * @returns {string}
     */
    formatMoney(cents) {
      if (typeof theme !== 'undefined' && theme.moneyFormat) {
        const format = theme.moneyFormat;
        const value = (cents / 100).toFixed(2);
        return format.replace('{{amount}}', value).replace('{{amount_no_decimals}}', Math.round(cents / 100));
      }
      // Fallback
      return `£${(cents / 100).toFixed(2)}`;
    }
  }

  customElements.define('bundle-products', BundleProducts);
}

